import type { Audit, PaginatedAuditResponse } from '@/api/admin/audit';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Pagination } from '@/components/ui/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface ProfileAuditModalProps {
  open: boolean;
  onClose: () => void;
  profileName: string;
  auditData: PaginatedAuditResponse | undefined;
  currentPage: number;
  onPageChange: (page: number) => void;
  isLoading: boolean;
}

// Field label mapping (Portuguese)
const fieldLabel: Record<string, string> = {
  name: 'nome',
  active: 'status',
  locked_control_code: 'permissões',
};

// Format field values for display
function formatValue(value: unknown, field?: string): string {
  if (typeof value === 'boolean') {
    if (field === 'active') return value ? 'ativo' : 'inativo';
    return value ? 'sim' : 'não';
  }

  if (value === null || value === undefined) {
    return '';
  }

  if (Array.isArray(value)) {
    if (field === 'locked_control_code') {
      return `${value.length} permissões`;
    }
    return value.join(', ');
  }

  if (typeof value === 'object') {
    return '';
  }

  return String(value);
}

// Format audit action for display
function formatAction(audit: Audit): React.ReactNode {
  const { type, metadata } = audit;

  if (type === 'create' || !metadata || metadata.length === 0) {
    return audit.action;
  }

  return (
    <div className="space-y-2">
      {metadata.map((m) => {
        const field = m.field;
        const pretty = fieldLabel[field] ?? field;
        const before = formatValue(m.before, field);
        const after = formatValue(m.after, field);

        // Handle active field
        if (field === 'active') {
          if (after === 'ativo') {
            return (
              <div key={`${field}-${after}`} className="text-sm">
                Perfil <span className="font-bold">ativado</span>
              </div>
            );
          }
          if (after === 'inativo') {
            return (
              <div key={`${field}-${after}`} className="text-sm">
                Perfil <span className="font-bold">inativado</span>
              </div>
            );
          }
        }

        // Handle locked_control_code field
        if (field === 'locked_control_code') {
          const beforeCount = Array.isArray(m.before) ? m.before.length : 0;
          const afterCount = Array.isArray(m.after) ? m.after.length : 0;
          return (
            <div key={`${field}-${beforeCount}-${afterCount}`} className="text-sm">
              Permissões alteradas de <span className="font-medium">{beforeCount} permissões</span>{' '}
              para <span className="font-medium">{afterCount} permissões</span>
            </div>
          );
        }

        // Default field change display
        return (
          <div key={`${field}-${before}-${after}`} className="text-sm">
            <span className="mr-1 font-bold break-all">{`Campo '${pretty}' de`}</span>
            <span className="font-medium break-words">{before}</span>
            <span className="mx-1 font-bold">para</span>
            <span className="font-medium break-words">{after}</span>
          </div>
        );
      })}
    </div>
  );
}

// Format date for display
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'medium',
  }).format(date);
}

function AuditTableSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 5 }, (_, i) => `skeleton-${i}`).map((key) => (
        <div key={key} className="flex gap-4">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 flex-1" />
        </div>
      ))}
    </div>
  );
}

export function ProfileAuditModal({
  open,
  onClose,
  profileName,
  auditData,
  currentPage,
  onPageChange,
  isLoading,
}: ProfileAuditModalProps) {
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Histórico de Alterações - {profileName}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <AuditTableSkeleton />
            </div>
          ) : auditData && auditData.docs.length > 0 ? (
            <div className="space-y-4">
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[150px]">Data/Hora</TableHead>
                      <TableHead className="w-[150px]">Usuário</TableHead>
                      <TableHead>Ação</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {auditData.docs.map((audit) => (
                      <TableRow key={audit.id}>
                        <TableCell className="text-sm text-gray-500">
                          {formatDate(audit.created_at)}
                        </TableCell>
                        <TableCell className="text-sm">{audit.user?.name ?? ''}</TableCell>
                        <TableCell>{formatAction(audit)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={auditData.pages || 1}
                totalItems={auditData.total || 0}
                limit={auditData.limit || 10}
                onPageChange={onPageChange}
              />
            </div>
          ) : (
            <div className="flex justify-center items-center h-64 text-gray-500">
              Nenhum registro de alteração encontrado.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
